import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { 
  Building2, FileText, Settings, LogOut, User, Palette, Save,
  Crown, BarChart3, Loader2, Building, Phone, Mail, HelpCircle, Gift, Sparkles, Calculator
} from "lucide-react";
import { toast } from "sonner";

const SettingsPage = ({ auth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  
  const [name, setName] = useState(auth.user?.name || "");
  const [companyName, setCompanyName] = useState(auth.user?.company_name || "");
  const [contactEmail, setContactEmail] = useState(auth.user?.contact_email || auth.user?.email || "");
  const [contactPhone, setContactPhone] = useState(auth.user?.contact_phone || "");
  const [brandColor, setBrandColor] = useState(auth.user?.brand_color || "#2DD4BF");

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name,
        company_name: companyName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        brand_color: brandColor
      }, { withCredentials: true });
      
      // Update local auth state
      auth.checkAuth();
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/auth/apply-promo`, {
        promo_code: promoCode.trim()
      }, { withCredentials: true });
      
      toast.success(response.data.message || "Promo code applied successfully!");
      setPromoCode("");
      auth.checkAuth(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid promo code");
    } finally {
      setPromoLoading(false);
    }
  };

  const tierBadgeColor = {
    trial: "bg-slate-100 text-slate-700",
    free: "bg-slate-100 text-slate-700",
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700",
    admin: "bg-amber-100 text-amber-700"
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/reports" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <FileText className="h-5 w-5" />
            <span>My Reports</span>
          </Link>
          <Link 
            to="/calculators" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Calculator className="h-5 w-5" />
            <span>Deal Calculators</span>
          </Link>
          <Link 
            to="/subscription" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Crown className="h-5 w-5" />
            <span>Subscription</span>
          </Link>
          <Link 
            to="/support" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <HelpCircle className="h-5 w-5" />
            <span>Support</span>
          </Link>
          <Link 
            to="/settings" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white"
          >
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className={`${tierBadgeColor[auth.user?.subscription_tier || "free"]} text-xs`}>
                  {auth.user?.subscription_tier?.toUpperCase() || "FREE"}
                </Badge>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
              onClick={auth.logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/reports")}>
              <FileText className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-3xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900 mb-2">
              Settings
            </h1>
            <p className="text-slate-600">
              Manage your account and report branding
            </p>
          </div>

          {/* Profile Settings */}
          <Card className="mb-6" data-testid="profile-settings-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center">
                <User className="h-5 w-5 mr-2 text-teal-500" />
                Profile
              </CardTitle>
              <CardDescription>Your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-4 mb-6">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={auth.user?.picture} />
                  <AvatarFallback className="bg-teal-500 text-navy-900 text-xl">
                    {auth.user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-navy-900">{auth.user?.email}</p>
                  <Badge className={tierBadgeColor[auth.user?.subscription_tier || "free"]}>
                    {auth.user?.subscription_tier?.toUpperCase() || "FREE"} Plan
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    data-testid="input-name"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding Settings */}
          <Card className="mb-6" data-testid="branding-settings-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center">
                <Palette className="h-5 w-5 mr-2 text-teal-500" />
                Report Branding
              </CardTitle>
              <CardDescription>Customize your shared reports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company/Business Name</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="companyName"
                    placeholder="Your Company LLC"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="pl-10"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="contactEmail"
                      type="email"
                      placeholder="contact@company.com"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className="pl-10"
                      data-testid="input-contact-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="contactPhone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      className="pl-10"
                      data-testid="input-contact-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="brandColor">Brand Color</Label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    id="brandColor"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="h-10 w-20 rounded cursor-pointer"
                    data-testid="input-brand-color"
                  />
                  <Input
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="w-32 font-mono"
                    maxLength={7}
                  />
                  <div 
                    className="h-10 flex-1 rounded-lg border border-slate-200"
                    style={{ backgroundColor: brandColor }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            onClick={handleSave}
            className="w-full bg-navy-900 hover:bg-navy-800"
            disabled={loading}
            data-testid="save-settings-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>

          {/* Promo Code Section */}
          {auth.user?.subscription_tier !== "admin" && (
            <Card className="mt-6 border-amber-200 bg-amber-50/50" data-testid="promo-code-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center">
                  <Gift className="h-5 w-5 mr-2 text-amber-500" />
                  Promotional Code
                </CardTitle>
                <CardDescription>Have a promo code? Enter it here to unlock special access</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-3">
                  <div className="flex-1 relative">
                    <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
                    <Input
                      placeholder="Enter promo code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="pl-10 uppercase"
                      data-testid="input-promo-code"
                    />
                  </div>
                  <Button
                    onClick={handleApplyPromo}
                    disabled={promoLoading || !promoCode.trim()}
                    className="bg-amber-500 hover:bg-amber-400 text-white"
                    data-testid="apply-promo-btn"
                  >
                    {promoLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin Badge for Owners */}
          {auth.user?.subscription_tier === "admin" && (
            <Card className="mt-6 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50" data-testid="admin-badge-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-full bg-amber-500">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-navy-900">Owner/Founder Access</h3>
                    <p className="text-sm text-slate-600">You have unlimited Pro access at no charge</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Danger Zone */}
          <Card className="mt-8 border-red-200" data-testid="danger-zone-card">
            <CardHeader>
              <CardTitle className="font-heading text-red-600">Danger Zone</CardTitle>
              <CardDescription>Irreversible actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-navy-900">Sign Out</p>
                  <p className="text-sm text-slate-500">Sign out of your account on this device</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => {
                    auth.logout();
                    navigate("/");
                  }}
                  data-testid="signout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
